import React from "react";
import { HtmlContent } from "@/components/RichText";

/**
 * Letter type code results (16-type style), driven by `result.type_code` from the backend
 * type_code resolver: a code badge for the hero card and a preference map showing how
 * strongly each letter was chosen.
 */

const ACCENT = "#D47A65";

const pick = (source, field, lang) => {
  const own = source?.[`${field}_${lang === "en" ? "en" : "id"}`];
  const other = source?.[`${field}_${lang === "en" ? "id" : "en"}`];
  return own || other || "";
};

/** Pole name for one side, e.g. "Introvert", falling back to its letter. */
export function typeCodePoleName(preference, side, lang = "id") {
  return pick(preference, `pole_${side}`, lang) || preference?.[`pole_${side}_code`] || "";
}

export function TypeCodeBadge({ typeCode, lang = "id", color }) {
  const letters = typeCode?.letters || [];
  if (!letters.length) return null;
  const tint = color || ACCENT;
  return (
    <div className="flex flex-col items-center gap-1" data-testid="type-code-badge">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {lang === "en" ? "Your type" : "Tipe kamu"}
      </span>
      <div className="flex items-center gap-1.5">
        {letters.map((letter, i) => (
          <span
            key={`${letter}-${i}`}
            className="inline-flex items-center justify-center min-w-9 h-9 px-2 rounded-xl font-display text-lg"
            style={{ background: tint, color: "#fff" }}
            title={typeCodePoleName(typeCode.preferences?.[i], typeCode.preferences?.[i]?.side, lang)}
          >
            {letter}
          </span>
        ))}
      </div>
    </div>
  );
}

function PreferenceRow({ preference, lang }) {
  const pct = Math.max(0, Math.min(100, Number(preference.percentage) || 0));
  const name = pick(preference, "name", lang) || preference.code;
  const low = typeCodePoleName(preference, "low", lang);
  const high = typeCodePoleName(preference, "high", lang);
  const chosen = preference.side === "high" ? high : low;
  const lowLabel = preference.pole_low_code && low !== preference.pole_low_code ? `${low} (${preference.pole_low_code})` : low;
  const highLabel = preference.pole_high_code && high !== preference.pole_high_code ? `${high} (${preference.pole_high_code})` : high;
  const interpretation = pick(preference.category, "interpretation", lang);
  return (
    <div className="p-4 md:p-5 rounded-2xl border border-border bg-card" data-testid={`type-code-pref-${preference.code}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-widest text-muted-foreground break-words">{name}</div>
        <div className="font-display text-lg text-teal-800" data-testid={`type-code-strength-${preference.code}`}>
          {Math.round(preference.strength)}% {chosen}
        </div>
      </div>
      <div className="mt-3">
        <div
          className="relative h-2.5 rounded-full"
          style={{ background: "linear-gradient(90deg, rgb(38 77 79 / 0.22), rgb(38 77 79 / 0.06) 50%, rgb(212 122 101 / 0.28))" }}
          role="img"
          aria-label={`${name}: ${Math.round(preference.strength)}% ${chosen}`}
        >
          <div className="absolute top-1/2 left-1/2 h-4 w-px -translate-y-1/2 bg-teal-800/25" aria-hidden />
          <div
            className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow"
            style={{ left: `${pct}%`, background: ACCENT }}
          />
        </div>
        <div className="mt-2 flex justify-between gap-3 text-[11px]">
          <span className={`break-words ${preference.side === "low" ? "text-teal-800 font-semibold" : "text-muted-foreground"}`}>{lowLabel}</span>
          <span className={`break-words text-right ${preference.side === "high" ? "text-teal-800 font-semibold" : "text-muted-foreground"}`}>{highLabel}</span>
        </div>
      </div>
      {interpretation && (
        <div className="mt-3 text-sm text-teal-800/90 leading-relaxed"><HtmlContent html={interpretation} /></div>
      )}
    </div>
  );
}

export function TypeCodeProfileSection({ typeCode, lang = "id" }) {
  const preferences = typeCode?.preferences || [];
  if (!preferences.length) return null;
  const ties = preferences.filter((p) => p.tie);
  return (
    <section className="mb-8 p-5 md:p-6 rounded-3xl bg-card border border-border" data-testid="type-code-profile-section">
      <div className="mb-4">
        <div className="font-display text-lg md:text-xl">{lang === "en" ? "Your Preferences" : "Preferensi Kamu"}</div>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          {lang === "en"
            ? "Each letter of your type is a preference, not a fixed box. The percentage shows how clearly you lean that way."
            : "Setiap huruf tipemu adalah kecenderungan, bukan kotak yang kaku. Persentasenya menunjukkan seberapa jelas kamu condong ke sisi itu."}
        </p>
      </div>
      <div className="space-y-3">
        {preferences.map((preference) => (
          <PreferenceRow key={preference.dimension_id} preference={preference} lang={lang} />
        ))}
      </div>
      {ties.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="type-code-tie-note">
          {lang === "en"
            ? `${ties.map((p) => pick(p, "name", lang) || p.code).join(", ")}: exactly balanced, so the letter follows the test's tie rule.`
            : `${ties.map((p) => pick(p, "name", lang) || p.code).join(", ")}: skornya seimbang persis, jadi hurufnya mengikuti aturan seri tes ini.`}
        </p>
      )}
    </section>
  );
}
